import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import "../../../assets/styles/custom-styles.css";
import { unitFields } from "../../../constants/forms/unitFields";
import useForm from "../../../hooks/useForm";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditUnitPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      abbreviation: [],
      deleted_at: false,
    },
    "equipments_units",
  );

  const {
    getByColumn: baseServiceGetById,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(unitFields),
  );

  // --- Query para buscar os detalhes da Unidade ---
  const {
    data: unitData,
    isLoading: isLoadingUnit,
    isError: isErrorUnit,
    error: unitQueryError,
  } = useQueryWithLoader({
    queryKey: ["equipment_unit", id],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetById(entities.units.getById(id));
      return response.result;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  useEffect(() => {
    if (unitData) {
      setFormData({
        unit: unitData,
      });
      hideLoader();
    }
  }, [unitData]);

  // --- Mutação para atualizar a Unidade ---
  const updateUnitMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.units.update(id),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        console.log("Unidade atualizada com sucesso");
        navigate(`/aluguel/unidades`, { state: { type: "success" } });
        queryClient.invalidateQueries({ queryKey: ["equipment_unit", id] });
        queryClient.invalidateQueries({
          queryKey: ["equipments_units", filters, pagination],
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        unit: {
          ...cleanedData.unit,
        },
      };
      await updateUnitMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar unidade:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateUnitMutation]);

  const handleBack = useCallback(() => {
    navigate("/aluguel/unidades");
  }, [navigate]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return unitFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Unidade"
        showBackButton={true}
        backUrl="/aluguel/unidades"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isLoadingUnit || updateUnitMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditUnitPage;
